import { Button, Card, Grid, Group, Tabs } from '@mantine/core';
import {
  IconBell,
  IconBrandWhatsapp,
  IconDeviceFloppy,
  IconMail,
  IconMessageCircle,
  IconPhone,
  IconSettings,
  IconWebhook,
} from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import LabeledSlider from '../../components/LabeledSlider';
import Metadata from '../../components/Metadata';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import ClickTrackingSection from '../../components/Settings/ClickTrackingSection';
import SettingsTabAppPush from '../../components/Settings/Tabs/SettingsTabAppPush';
import SettingsTabEmails from '../../components/Settings/Tabs/SettingsTabEmails';
import SettingsTabMessage from '../../components/Settings/Tabs/SettingsTabMessage';
import SettingsTabSubscriptionManagement from '../../components/Settings/Tabs/SettingsTabSubscriptionManagement';
import Spinner from '../../components/Spinner';
import { CLICK_TRACKING_SUBDOMAIN_REGEX } from '../../constants/clickTracking';
import { Page, SettingsDomainTabs } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';
import { setDomain as setDomainRedux } from '../../store/domainSlice';

const SettingsCommunicationMessaging = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();

  const currentDomain = useSelector((state) => state.domain.domain);
  const currentAccount = useSelector((state) => state.account.account);

  const { accountId, domainId } = useParams();

  const [domain, setDomain] = useState(currentDomain);
  const [error, setError] = useState(false);
  const [testResult, setTestResult] = useState({});
  const [hasFirebaseChanges, setHasFirebaseChanges] = useState(false);

  const firebaseSettingsRef = useRef();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const toggleConfirmationModal = () => {
    setShowConfirmationModal(!showConfirmationModal);
  };

  const search = queryString.parse(location.search);
  let tab = search.tab || 'general';

  const domainChanged = JSON.stringify(currentDomain) !== JSON.stringify(domain);

  useEffect(() => {
    setDomain(currentDomain);
  }, [currentDomain]);

  const validateDomain = (domain, domainChanged) => {
    if (!domain.name) return false;

    if (!domain.url) return false;

    if (domain.clickTrackingSubdomain && !CLICK_TRACKING_SUBDOMAIN_REGEX.test(domain.clickTrackingSubdomain)) {
      return false;
    }

    if (!domainChanged) return false;

    return true;
  };

  if (parseInt(domain?.id) !== parseInt(domainId)) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const testUrl = (suffix, url) => {
    setTestResult({ [suffix]: {} });
    setError(undefined);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/${suffix}`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    })
      .then((result) => {
        setTestResult({ [suffix]: { response: result.response } });
      })
      .catch((e) => setTestResult({ [suffix]: { error: e.message } }));
  };
  const handleChange = (e) => {
    setDomain({ ...domain, [e.target.name]: e.target.value });
  };

  const submitForm = async (cb) => {
    if (showConfirmationModal) {
      cb();
    }

    setError(undefined);
    try {
      const hadFirebaseChanges = hasFirebaseChanges;

      // Save Firebase changes first
      if (hadFirebaseChanges && firebaseSettingsRef.current) {
        await firebaseSettingsRef.current.saveChanges();
        setHasFirebaseChanges(false);
      }

      // Save domain changes or refresh if only Firebase changes were made
      let result;
      if (domainChanged) {
        result = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}`, {
          method: 'PUT',
          body: JSON.stringify(domain),
        });
        dispatch(setDomainRedux(result.domain));
      } else if (hadFirebaseChanges) {
        // Refresh domain to get updated appType
        result = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}`);
      }
      if (result?.domain) {
        dispatch(setDomainRedux(result.domain));
      }
    } catch (e) {
      setError(e);
    }
  };

  const setTab = (tab) => {
    navigate(location.pathname.replace(`/settings/messagingAndAds`, `/settings/messagingAndAds?tab=${tab}`));
  };

  const buttonHandler = () => {
    if (domainChanged || hasFirebaseChanges) {
      submitForm();
    }
  };

  return (
    <div>
      <Metadata title={`${Page['settings']} | ${SettingsDomainTabs[tab]}`} />
      <Grid pt={30} my={20}>
        <Grid.Col span={12}>
          <form onSubmit={(e) => submitForm(e)}>
            <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
              <Tabs.List>
                <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>
                  General
                </Tabs.Tab>
                <Tabs.Tab value="emails" data-tour="shopSettingsGeneralTab" leftSection={<IconMail size={16} />}>
                  Emails
                </Tabs.Tab>
                <Tabs.Tab value="textMessage" leftSection={<IconMessageCircle size={16} />}>
                  Text Messages
                </Tabs.Tab>
                <Tabs.Tab value="viberMessage" leftSection={<IconPhone size={16} />}>
                  Viber Messages
                </Tabs.Tab>
                <Tabs.Tab value="pushNotification" leftSection={<IconBell size={16} />}>
                  Push Notifications
                </Tabs.Tab>
                <Tabs.Tab value="webhooks" leftSection={<IconWebhook size={16} />}>
                  Webhooks
                </Tabs.Tab>
                <Tabs.Tab value="whatsapp" leftSection={<IconBrandWhatsapp size={16} />}>
                  WhatsApp
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="general">
                <Card withBorder>
                  <Card.Section p="md">
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                        <label>Max Messages Per Day (across all channels)</label>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 8 }}>
                        <LabeledSlider
                          min={1}
                          max={30}
                          value={domain.maxMessagesPerDay}
                          onChange={(value) => setDomain({ ...domain, maxMessagesPerDay: value })}
                        />
                      </Grid.Col>
                    </Grid>
                  </Card.Section>
                </Card>
                {tab === 'general' && (
                  <ClickTrackingSection domain={domain} currentDomain={currentDomain} handleChange={handleChange} />
                )}
                {tab === 'general' && (
                  <SettingsTabSubscriptionManagement
                    domain={domain}
                    currentDomain={currentDomain}
                    handleChange={handleChange}
                    testResult={testResult}
                    testUrl={testUrl}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="emails">
                <SettingsTabEmails
                  domain={domain}
                  currentDomain={currentDomain}
                  setDomain={setDomain}
                  currentAccount={currentAccount}
                  handleChange={handleChange}
                />
              </Tabs.Panel>
              <Tabs.Panel value="textMessage">
                {tab === 'textMessage' && (
                  <SettingsTabMessage domain={domain} handleChange={handleChange} type="text" />
                )}
              </Tabs.Panel>
              <Tabs.Panel value="viberMessage">
                {tab === 'viberMessage' && (
                  <SettingsTabMessage domain={domain} handleChange={handleChange} type="viber" />
                )}{' '}
              </Tabs.Panel>

              <Tabs.Panel value="pushNotification">
                <SettingsTabAppPush ref={firebaseSettingsRef} domain={domain} onChange={setHasFirebaseChanges} />
                <Card withBorder mt="md">
                  <Card.Section p="md">
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                        <label>Max Push Notifications Per Day</label>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 8 }}>
                        <LabeledSlider
                          min={1}
                          max={10}
                          value={domain.maxPushNotificationsPerDay}
                          onChange={(value) => setDomain({ ...domain, maxPushNotificationsPerDay: value })}
                        />
                      </Grid.Col>
                    </Grid>
                  </Card.Section>
                </Card>
              </Tabs.Panel>

              <Tabs.Panel value="webhooks">
                <Card withBorder>
                  <Card.Section p="md">
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                        <label>Max Webhooks Per Day</label>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 8 }}>
                        <LabeledSlider
                          min={1}
                          max={10}
                          value={domain.maxWebhooksPerDay}
                          onChange={(value) => setDomain({ ...domain, maxWebhooksPerDay: value })}
                        />
                      </Grid.Col>
                    </Grid>
                  </Card.Section>
                </Card>
              </Tabs.Panel>

              <Tabs.Panel value="whatsapp">
                <Card withBorder>
                  <Card.Section p="md">
                    <Grid mb="md">
                      <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                        <label>Max WhatsApp Messages Per Day</label>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 8 }}>
                        <LabeledSlider
                          min={1}
                          max={10}
                          value={domain.maxWhatsappMessagesPerDay}
                          onChange={(value) => setDomain({ ...domain, maxWhatsappMessagesPerDay: value })}
                        />
                      </Grid.Col>
                    </Grid>
                  </Card.Section>
                </Card>
              </Tabs.Panel>
            </Tabs>

            <Group justify="flex-end" gap="xs" py="md">
              <Button
                disabled={!validateDomain(domain, domainChanged) && !hasFirebaseChanges}
                type="button"
                onClick={buttonHandler}
              >
                <IconDeviceFloppy size={14} /> {domainChanged || hasFirebaseChanges ? 'Save' : 'Saved'}
              </Button>
            </Group>
            {showConfirmationModal && (
              <ConfirmationModal
                show={showConfirmationModal}
                close={(cb) => toggleConfirmationModal(cb)}
                confirm={(cb) => submitForm(cb)}
                message="Please note that modifying your Facebook Ad Account will trigger the creation of new Facebook Ads reflecting the revised settings."
              />
            )}
          </form>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default SettingsCommunicationMessaging;
